import clsx from 'clsx';
import React from 'react';

import { SecondaryButton } from '@components/Button/variants';
import useEventTracking from '@hooks/useEventTracking';
import { new_session_path, subscribe_path } from '@routes';
import { ClickTarget, click } from '@tracking';

const PERKS = [
  { label: 'Today’s five polls + results', subscriberOnly: false, tag: 'Free' },
  { label: 'Archive: the past week of polls', subscriberOnly: true, tag: 'Subscribers' },
  {
    label: 'Full access to The Information’s reporting',
    subscriberOnly: true,
    tag: 'Subscribers',
  },
];

const ANNUAL_OFFER_CODE = 'polls-app-lp-annual-299';
const SUBSCRIBE_CLICK_TEXT = 'Subscribe for $299 (25% off)';

const ForSubscribers = () => {
  const { trackEvent } = useEventTracking();

  return (
    <section
      aria-labelledby="polls-for-subscribers"
      className="bg-background-white py-48 sm:py-64 md:py-80 xl:py-96"
    >
      <div className="mx-auto w-full max-w-[1440px] px-20 sm:px-40 lg:px-64 xl:px-80">
        <div className="flex flex-col gap-40 md:flex-row md:items-center md:justify-between md:gap-64">
          <div className="md:flex-1">
            <p className="m-0 font-suisse-intl-mono text-[14px] uppercase leading-none tracking-widest text-primary-magenta">
              For subscribers
            </p>
            <h2
              className="m-0 mt-20 font-suisse-intl text-[30px] font-bold leading-[1.1] tracking-tight text-grayscale-charcoal sm:text-[36px] md:text-[40px] xl:text-[46px]"
              id="polls-for-subscribers"
            >
              Want the whole week?
            </h2>
            <p className="m-0 mt-24 font-suisse-intl text-[17px] font-normal leading-[1.55] text-grayscale-charcoal sm:text-[18px] xl:text-[20px]">
              Today’s five are free. Subscribers get the Archive: every question and result from the
              past seven days, included with a subscription at no extra cost.
            </p>
            <div className="mt-32 flex flex-wrap items-center gap-x-24 gap-y-12">
              <SecondaryButton
                onClickCapture={() =>
                  trackEvent(
                    click({ clickTarget: ClickTarget.Subscribe, clickText: SUBSCRIBE_CLICK_TEXT })
                  )
                }
                as="link"
                href={subscribe_path({ offer: ANNUAL_OFFER_CODE })}
                size="small"
                className="!w-fit !rounded-full !border-primary-magenta !px-32 !py-16 !text-[16px] !font-bold !text-grayscale-charcoal !no-underline hover:!border-primary-magenta hover:!bg-primary-magenta hover:!text-white focus:!bg-primary-magenta focus:!text-white"
              >
                {SUBSCRIBE_CLICK_TEXT}
              </SecondaryButton>
              <div>
                <a
                  href={new_session_path()}
                  className="font-suisse-intl text-[15px] font-normal leading-[1.5] text-grayscale-gray hover:text-primary-magenta"
                >
                  Already a subscriber?
                </a>{' '}
                <span className="m-0 font-suisse-intl text-[15px] font-normal leading-[1.5] text-grayscale-gray">
                  Your Archive is waiting in the app.
                </span>
              </div>
            </div>
          </div>
          <div className="rounded-2 bg-[#2f3538] p-24 sm:p-32 md:w-[51%] md:shrink-0 xl:p-40">
            <p className="m-0 font-suisse-intl-mono text-[13px] uppercase leading-none tracking-widest text-[#9a959c]">
              What you get
            </p>
            <ul className="m-0 mt-24 list-none p-0">
              {PERKS.map(({ label, subscriberOnly, tag }) => (
                <li
                  className="flex items-center justify-between gap-16 border-0 border-t border-solid border-[#454a4c] py-16 first:border-t-0 first:pt-0"
                  key={label}
                >
                  <span className="font-suisse-intl text-[16px] leading-[1.5] text-white md:text-[18px]">
                    {label}
                  </span>
                  <span
                    className={clsx(
                      'shrink-0 whitespace-nowrap font-suisse-intl-mono text-[12px] uppercase leading-none tracking-widest',
                      subscriberOnly ? 'text-primary-magenta' : 'text-[#9a959c]'
                    )}
                  >
                    {tag}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForSubscribers;
